import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { AgilityTableExportService } from '@agility-components/agility-table-export/services/agility-table-export.service';
import {
  AccountingAdjustmentReasonType,
  LedgerTreasuryAdjustmentReasonType,
  LedgerWhpAndOpAdjustmentReasonType,
} from '../../../graphql/generated';
import { DecimalPipe } from '@angular/common';
import { Nullable } from '../../../utils/types/utility.types';
import { ColumnHeader } from '@models/wire-recipient-pagination.interface';
import { AdjustmentReasonService } from '@services/adjustment-reason.service';
import { UtilsService } from '@services/utils/utils.service';
import {
  COLUMN_KEYS,
  defaultPercentageBlankWhenTransactionIn,
  TABLE_INFO,
} from './agility-export.constant';
import {
  applyCurrencyExportCell,
  cloneRowApplyPercentageStep,
  getPercentageExportFieldKeys,
} from './agility-export-formatting';
import {
  AnalyticsEvents,
  AnalyticsService,
} from 'src/app/analytics/services/analytics/analytics.service';
import { DateUtilsService } from '@services/utils/date-utils/date-utils.service';

type AdjustmentReasonType = Nullable<
  | AccountingAdjustmentReasonType
  | LedgerTreasuryAdjustmentReasonType
  | LedgerWhpAndOpAdjustmentReasonType
>;

@Component({
  selector: 'app-agility-export-button',
  templateUrl: './agility-export-button.component.html',
  standalone: false,
})
export class AgilityExportButtonComponent implements OnInit {
  private readonly agilityTableExportService = inject(
    AgilityTableExportService
  );

  @Input() selectedData: any[] = [];
  @Input() columnHeaders: ColumnHeader[] = [];
  @Input() tableId: Table | undefined;
  @Input() disableExport!: boolean;
  @Output() exportCsvEvent = new EventEmitter<any>();
  @Input() pdfButton: boolean = true;
  additionalActions: Array<MenuItem> = [];
  @Input() exportName!: string;
  /** When set, columns with `type: 'percentage'` render blank if `row.transaction` is in this list (e.g. Adjustment/Collection on Interest). */
  @Input() percentageBlankWhenTransactionIn: string[] =
    defaultPercentageBlankWhenTransactionIn();

  exportColumns: any;
  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly utilsService: UtilsService,
    private readonly adjustmentReasonService: AdjustmentReasonService,
    private readonly decimalPipe: DecimalPipe,
    private readonly dateUtilsService: DateUtilsService
  ) {}

  ngOnInit(): void {
    this.additionalActions = this.getAdditionalActions();
  }

  getAdditionalActions(): MenuItem[] {
    return [
      {
        label: 'Export Table',
        items: [
          {
            id: 'exportPdf',
            label: 'Download PDF File',
            icon: 'pi pi-file-pdf',
            visible: this.pdfButton,
            command: () => {
              this.exportPdf(this.exportName);
            },
          },
          {
            id: 'exportCsv',
            label: 'Download CSV File',
            icon: 'pi pi-file-excel',
            command: () => {
              this.analyticsService.trackEvent(AnalyticsEvents.EXPORT_CSV, {
                element_id: 'export-csv-button',
                feature: 'agility-export-button',
                label: this.exportName,
              });

              if (!this.tableId) {
                return;
              }

              this.tableId['_columns'] = this.columnHeaders;
              this.tableId['_selection'] = this.formatSelectedRowData();
              this.tableId.exportCSV({
                selectionOnly: true,
              });
            },
          },
        ],
      },
    ];
  }

  formatSelectedRowData() {
    const currencyFields: string[] =
      this.columnHeaders
        ?.filter((col: { type?: string }) => col?.type === 'currency')
        ?.map((col: { field: string }) => col?.field) || [];
    const percentageFields = getPercentageExportFieldKeys(this.columnHeaders);
    return (
      this.selectedData?.map((row: { [key: string]: string | number }) => {
        const updatedRow = cloneRowApplyPercentageStep(
          row,
          percentageFields,
          this.percentageBlankWhenTransactionIn,
          this.decimalPipe
        );

        currencyFields?.forEach((key) => {
          applyCurrencyExportCell(updatedRow, key, (n) =>
            this.utilsService.getCurrency(n)
          );
        });
        // Transform adjustment reason field to display value
        if (updatedRow['adjustmentReason'] !== undefined) {
          const adjustmentReason = updatedRow[
            'adjustmentReason'
          ] as AdjustmentReasonType;

          if (
            this.agilityTableExportService.isAccountingAdjustmentReason(
              adjustmentReason
            )
          ) {
            updatedRow['adjustmentReason'] =
              this.adjustmentReasonService.convertAccountingEnumToDisplayValue(
                adjustmentReason
              );
          } else if (
            this.agilityTableExportService.isLedgerTreasuryAdjustmentReason(
              adjustmentReason
            )
          ) {
            updatedRow['adjustmentReason'] =
              this.adjustmentReasonService.convertLedgerTreasuryEnumToDisplayValue(
                adjustmentReason
              );
          } else if (
            this.agilityTableExportService.isLedgerWhpAndOpAdjustmentReason(
              adjustmentReason
            )
          ) {
            updatedRow['adjustmentReason'] =
              this.adjustmentReasonService.convertLedgerWhpAndOpEnumToDisplayValue(
                adjustmentReason
              );
          }
        }

        /**
         * Format date fields for export
         */
        const exportDateFieldNames = [
          'date',
          'createdon',
          'updatedon',
          'cancelledon',
          'submittedon',
          'dob',
          'timestamp',
        ];
        for (const key of Object.keys(updatedRow)) {
          if (
            !exportDateFieldNames.some((fieldName) =>
              key.toLowerCase().includes(fieldName)
            )
          ) {
            continue;
          }
          const dateValue = updatedRow[key];
          updatedRow[key] = this.dateUtilsService.formatDateForExport(
            typeof dateValue === 'string' ? dateValue : ''
          );
        }

        return updatedRow;
      }) ?? []
    );
  }

  exportPdf(exportName: string) {
    this.analyticsService.trackEvent(AnalyticsEvents.EXPORT_PDF, {
      element_id: 'export-pdf-button',
      feature: 'agility-export-button',
      label: exportName,
    });

    this.exportColumns = this.columnHeaders.map(
      (col: { header: any; field: any }) => ({
        title: col.header,
        dataKey: col.field,
      })
    );
    this.agilityTableExportService
      .loadPdfLibraries()
      .then(({ jsPDF, autoTable }) => {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm' });
        autoTable(doc, {
          didParseCell: (hookData: any) => this.parsePdfCells(hookData),
          columns: this.exportColumns,
          head: [this.exportColumns.map((col: { title: any }) => col.title)],
          body: this.formatSelectedRowData(),
        });
        const filename = `${exportName}.pdf`;
        doc.save(filename);
      });
  }
  parsePdfCells(hookData: any) {
    if (hookData.section === TABLE_INFO.BODY_SECTION) {
      if (
        hookData.column.dataKey === COLUMN_KEYS.NOTE_AMOUNT ||
        hookData.column.dataKey === COLUMN_KEYS.WIRE_AMOUNT ||
        hookData.column.dataKey === COLUMN_KEYS.WAREHOUSE_PRINCIPAL
      ) {
        hookData.cell.styles.halign = TABLE_INFO.CELL_RIGHT_ALIGN;
      }
    }
  }
}
